import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { Pencil, Trash2, Power, PowerOff } from "lucide-react";

import { useTran } from "@/hooks";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { INDUSTRIAL_CLASSIFICATION, ROLES } from "@/lib/constants";
import { useOrganizationStore } from "../hooks";
import {
  getUnitTypeMeta,
  isLevelAbove,
  hasInactiveAncestor,
} from "../utils/organization.helpers";
import { useAuthStore } from "@/features/auth/hooks";
import { Can } from "@/hooks/useCan/Can";
import OrgStatusTag from "./OrgStatusTag";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const DetailRow = ({ label, value }) => (
  <div className="space-y-1 rounded-radius border border-border p-3 text-sm">
    <p className="text-xs uppercase tracking-wide text-muted-foreground">
      {label}
    </p>
    <p className="break-words text-sm font-medium text-foreground">
      {value ?? "-"}
    </p>
  </div>
);

const Breadcrumbs = ({ trail = [], activeColor }) => {
  if (!trail.length) return null;
  const lastIndex = trail.length - 1;

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
      {trail.map((crumb, index) => {
        const isLast = index === lastIndex;

        return (
          <span key={crumb.id} className="flex items-center gap-2">
            <span
              className={`px-2 py-1 text-[11px] ${
                isLast ? activeColor : "text-muted-foreground"
              }`}
            >
              {crumb.name}
            </span>

            {!isLast ? (
              <span className="text-muted-foreground">&gt;</span>
            ) : null}
          </span>
        );
      })}
    </div>
  );
};

const OrganizationDetails = observer(({ onEdit, onDelete, onToggleStatus }) => {
  const { t } = useTran();
  const { selectedUnit, loading, togglingId, unitsById } =
    useOrganizationStore();
  const industryLabelKey = selectedUnit?.industryLabel;

  const { user, userRole } = useAuthStore();
  const permissions = user?.permissions || [];
  const isSuperAdmin = userRole === ROLES.SUPER_ADMIN;

  const industryLabel = useMemo(() => {
    if (!industryLabelKey) return t("Not set");
    return (
      INDUSTRIAL_CLASSIFICATION.find((c) => c.key === industryLabelKey)
        ?.value || industryLabelKey
    );
  }, [industryLabelKey, t]);

  if (loading && !selectedUnit) {
    return (
      <Card className="h-full">
        <CardHeader className="space-y-2">
          <div className="h-5 w-1/3 animate-pulse rounded-full bg-muted" />
          <div className="h-4 w-1/2 animate-pulse rounded-full bg-muted/70" />
        </CardHeader>
        <CardContent className="space-y-3">
          {Array.from({ length: 4 }).map((_, idx) => (
            <div
              key={idx}
              className="h-16 animate-pulse rounded-radius bg-muted/60"
            />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (!selectedUnit) {
    return (
      <Card className="flex h-full flex-col items-center justify-center border-dashed p-6 text-center">
        <p className="text-base font-semibold text-foreground">
          {t("Select a unit to view details")}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          {t(
            "Highlight a node from the tree to inspect metadata, region, and hierarchy.",
          )}
        </p>
      </Card>
    );
  }

  const meta = getUnitTypeMeta(selectedUnit.unitLevel);
  const activateBlocked =
    selectedUnit.status === "inactive" &&
    hasInactiveAncestor(unitsById, selectedUnit.parentId);

  return (
    <Card className="h-full border-border">
      <CardHeader className="px-4 py-3 sm:px-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <span
                className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-background shadow-sm ${meta.color}`}
              >
                <meta.icon className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <CardTitle className="break-words text-base text-foreground">
                  {selectedUnit.name}
                </CardTitle>
                {selectedUnit.code &&
                !isLevelAbove(selectedUnit.unitLevel, 2) ? (
                  <div className="mt-0.5 flex flex-wrap items-center gap-2">
                    <p className="break-words text-xs font-medium text-primary">
                      {t("Code")}: {selectedUnit.code}
                    </p>
                    <OrgStatusTag
                      status={selectedUnit.status}
                      inactiveDate={selectedUnit.inactiveDate}
                    />
                  </div>
                ) : (
                  <OrgStatusTag
                    status={selectedUnit.status}
                    inactiveDate={selectedUnit.inactiveDate}
                  />
                )}
              </div>
            </div>
          </div>

          {selectedUnit.unitLevel === "parent_company" ? null : (
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap">
              <Can
                resource="organization"
                action="update"
                userPermissions={permissions}
              >
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  icon={Pencil}
                  className="w-full sm:w-auto"
                  onClick={() => onEdit?.(selectedUnit)}
                >
                  {t("Edit")}
                </Button>
              </Can>

              {isSuperAdmin ? (
                <div className="flex w-full flex-col gap-1 sm:w-auto">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    icon={selectedUnit.status === "inactive" ? Power : PowerOff}
                    className="w-full sm:w-auto"
                    disabled={togglingId === selectedUnit.id || activateBlocked}
                    onClick={() => onToggleStatus?.(selectedUnit)}
                  >
                    {selectedUnit.status === "inactive"
                      ? t("Activate")
                      : t("Deactivate")}
                  </Button>
                  {activateBlocked ? (
                    <p className="text-xs text-muted-foreground">
                      {t("A parent unit is still inactive")}
                    </p>
                  ) : null}
                </div>
              ) : null}

              <Can
                resource="organization"
                action="delete"
                userPermissions={permissions}
              >
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  icon={Trash2}
                  className="w-full sm:w-auto"
                  onClick={() => onDelete?.(selectedUnit)}
                >
                  {t("Delete")}
                </Button>
              </Can>
            </div>
          )}
        </div>

        <Breadcrumbs
          trail={selectedUnit.breadcrumbs}
          activeColor={meta.color}
        />
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <DetailRow
            label={t("Unit Level")}
            value={(() => {
              const m = /^level_(\d+)$/.exec(selectedUnit.unitLevel);
              return m ? `${t("Level")} ${m[1]}` : t(meta.label);
            })()}
          />
          <DetailRow
            label={t("Unit Level Name")}
            value={selectedUnit.unitLevelName || "-"}
          />
          <DetailRow
            label={t("Region")}
            value={selectedUnit.regionLabel || selectedUnit.region || "-"}
          />
          <DetailRow
            label={t("Country")}
            value={selectedUnit.countryLabel || selectedUnit.country || "-"}
          />
          {selectedUnit.industryLabel ? (
            <DetailRow label={t("Industry")} value={industryLabel || "-"} />
          ) : null}
        </div>

        {selectedUnit.address ? (
          <div className="rounded-radius border border-border p-3 text-sm">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              {t("Address")}
            </p>
            <p className="mt-1 whitespace-pre-line break-words text-sm font-medium text-foreground">
              {selectedUnit.address}
            </p>
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-5 text-xs mx-2 text-muted-foreground sm:grid-cols-2">
          <p>
            {t("Last updated")}:{" "}
            <span className="font-medium text-foreground">
              {formatDate(selectedUnit.updatedAt)}
            </span>
          </p>
          <p>
            {t("Created")}:{" "}
            <span className="font-medium text-foreground">
              {formatDate(selectedUnit.createdAt)}
            </span>
          </p>
        </div>
      </CardContent>
    </Card>
  );
});

export default OrganizationDetails;
